import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { cn } from '@/shared/lib/utils';

interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Primary and secondary actions, rendered end-aligned. */
  actions?: React.ReactNode;
  /** Renders a back link above the title. For detail pages. */
  backTo?: { to: string; label: string };
  className?: string;
}

/**
 * The title block for a page.
 *
 * Compact by design — an operational admin should not spend a third of the
 * viewport on a heading (§62). One `h1`, one line of description, actions on
 * the same row at desktop width and wrapping below it on mobile.
 */
export function PageHeader({ title, description, actions, backTo, className }: PageHeaderProps) {
  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {backTo && (
        <Button
          asChild
          variant="ghost"
          size="sm"
          className="text-muted-foreground -ms-2 h-7 w-fit gap-1.5 px-2 text-xs"
        >
          <Link to={backTo.to}>
            {/* Flips automatically under dir="rtl" (§76). */}
            <ArrowLeft className="size-3.5 rtl:rotate-180" aria-hidden />
            {backTo.label}
          </Link>
        </Button>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-1">
          <h1 className="font-display truncate text-xl font-bold tracking-tight">{title}</h1>
          {description && (
            <p className="text-muted-foreground text-xs leading-relaxed">{description}</p>
          )}
        </div>

        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}
